/**
 * useFeature Hook
 * Check if a feature/module is active for the current user's tenant
 */

import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'

interface TenantFeatures {
  features: string[]
  plan: string
}

/**
 * Hook to check if a feature is active for the current user
 * 
 * @param featureName - Feature name (e.g., "MODULE_SKETCHUP")
 * @returns boolean indicating if feature is active
 */
export function useFeature(featureName: string): boolean {
  const { user } = useAuthStore()
  
  // Admin: is_superuser, geen tenant_id, is_admin of role=ADMIN → altijd alle modules.
  // Behalve MODULE_SKETCHUP: daar geldt het pakket ook voor eigenaar en admin, alleen het platform
  // (is_superuser) mag erdoor (besluit Dirk 27-10, zelfde lijst als rbac.MODULES_ZONDER_TENANTADMIN_UITZONDERING).
  const zonderTenantadminUitzondering = featureName === 'MODULE_SKETCHUP'
  if (
    user?.is_superuser ||
    (!zonderTenantadminUitzondering && (
      !user?.tenant_id ||
      user?.is_admin ||
      String(user?.role || '').toUpperCase() === 'ADMIN'
    ))
  ) {
    return true
  }

  const { data: subscription } = useQuery<TenantFeatures>({
    queryKey: ['tenant-subscription', user?.tenant_id],
    queryFn: async () => {
      try {
        const response = await api.get('/billing/subscription')
        return response.data
      } catch (error) {
        // If subscription endpoint doesn't exist, return empty features
        console.warn('Could not fetch subscription:', error)
        return { features: [], plan: 'BASIC' }
      }
    },
    enabled: !!user?.tenant_id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false
  })
  
  if (!subscription || !subscription.features) {
    return false
  }
  
  return subscription.features.includes(featureName)
}

/**
 * Hook to get all active features for the current user
 * 
 * @returns array of active feature names
 */
export function useFeatures(): string[] {
  const { user } = useAuthStore()
  
  // Admin: is_superuser, geen tenant_id, is_admin of role=ADMIN → altijd alle modules
  if (
    user?.is_superuser ||
    !user?.tenant_id ||
    user?.is_admin ||
    String(user?.role || '').toUpperCase() === 'ADMIN'
  ) {
    return ['MODULE_SKETCHUP', 'MODULE_VECTORWORKS', 'MODULE_B2B_PROJECTS']
  }
  
  const { data: subscription } = useQuery<TenantFeatures>({
    queryKey: ['tenant-subscription', user?.tenant_id],
    queryFn: async () => {
      try {
        const response = await api.get('/billing/subscription')
        return response.data
      } catch (error) {
        console.warn('Could not fetch subscription:', error)
        return { features: [], plan: 'BASIC' }
      }
    },
    enabled: !!user?.tenant_id,
    staleTime: 5 * 60 * 1000,
    retry: false
  })
  
  return subscription?.features || []
}
